import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const FIRST_LOGIN_MESSAGES = [
  "Mitraa is glad you're here. Something wonderful is about to begin.",
  "Every great journey starts with the first step. Yours starts today with Mitraa.",
];

const RETURNING_MESSAGES = [
  "Mitraa is happy to see you today.",
  "Ready when you are.",
  "Your learning journey continues.",
  "Something good is waiting for you today.",
];

// 20 subtle stars — identical to StudentLogin for visual continuity
const STARS = [
  { left: "6%",  top: "4%",  size: 1,   opacity: 0.50 },
  { left: "14%", top: "10%", size: 1.5, opacity: 0.40 },
  { left: "22%", top: "6%",  size: 1,   opacity: 0.60 },
  { left: "31%", top: "14%", size: 1,   opacity: 0.35 },
  { left: "43%", top: "3%",  size: 1.5, opacity: 0.50 },
  { left: "55%", top: "9%",  size: 1,   opacity: 0.45 },
  { left: "65%", top: "16%", size: 1.5, opacity: 0.55 },
  { left: "74%", top: "5%",  size: 1,   opacity: 0.40 },
  { left: "83%", top: "20%", size: 1.5, opacity: 0.60 },
  { left: "92%", top: "11%", size: 1,   opacity: 0.35 },
  { left: "4%",  top: "26%", size: 1,   opacity: 0.45 },
  { left: "17%", top: "32%", size: 1.5, opacity: 0.30 },
  { left: "28%", top: "38%", size: 1,   opacity: 0.55 },
  { left: "39%", top: "22%", size: 1.5, opacity: 0.40 },
  { left: "51%", top: "44%", size: 1,   opacity: 0.35 },
  { left: "63%", top: "29%", size: 1,   opacity: 0.50 },
  { left: "76%", top: "37%", size: 1.5, opacity: 0.45 },
  { left: "85%", top: "47%", size: 1,   opacity: 0.30 },
  { left: "9%",  top: "48%", size: 1,   opacity: 0.40 },
  { left: "48%", top: "15%", size: 1,   opacity: 0.50 },
];

export default function WelcomeScreen() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const leavingRef = useRef(false);

  // Pick a stable random message once on mount
  const [message] = useState(() => {
    const isFirst = state?.is_first_login;
    const pool = isFirst ? FIRST_LOGIN_MESSAGES : RETURNING_MESSAGES;
    return pool[Math.floor(Math.random() * pool.length)];
  });

  const [visible, setVisible] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const [leaving, setLeaving] = useState(false);

  const doNavigate = useCallback(() => {
    if (leavingRef.current) return;
    leavingRef.current = true;
    setLeaving(true);
    setTimeout(() => navigate("/home", { replace: true }), 500);
  }, [navigate]);

  useEffect(() => {
    if (!state) {
      navigate("/home", { replace: true });
      return;
    }
    const t1 = setTimeout(() => setVisible(true), 40);
    const t2 = setTimeout(() => setShowButton(true), 1000);
    const t3 = setTimeout(doNavigate, 5000);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const isFirst = state?.is_first_login;
  const firstName = (state?.name || "").split(" ")[0] || "friend";

  return (
    <div
      data-testid="welcome-screen"
      style={{
        background: "radial-gradient(ellipse 100% 20% at 50% 105%, rgba(210, 100, 30, 0.35) 0%, transparent 100%), linear-gradient(to bottom, #0d1b2a 0%, #0f1d30 40%, #111c2e 100%)",
        opacity: leaving ? 0 : visible ? 1 : 0,
        transition: "opacity 0.5s ease",
      }}
      className="relative min-h-screen flex items-center justify-center px-4 py-10 overflow-hidden"
    >
      {/* Stars — same as StudentLogin */}
      {STARS.map((s, i) => (
        <div
          key={i}
          aria-hidden="true"
          style={{
            position: "absolute",
            left: s.left,
            top: s.top,
            width: `${s.size}px`,
            height: `${s.size}px`,
            borderRadius: "50%",
            background: "#ffffff",
            opacity: s.opacity,
            pointerEvents: "none",
          }}
        />
      ))}

      {/* Card */}
      <div
        style={{
          opacity: visible && !leaving ? 1 : 0,
          transform: visible && !leaving ? "translateY(0px)" : "translateY(16px)",
          transition: "opacity 0.7s ease 0.08s, transform 0.7s ease 0.08s",
          maxWidth: 480,
          width: "100%",
        }}
        className="relative z-10 bg-white rounded-3xl shadow-xl px-10 py-12 text-center"
      >
        {/* Brand logo */}
        <img src="/mitraa-logo-transparent.webp" alt="Mitraa" className="w-16 h-16 object-contain mx-auto mb-6" />

        <h1
          data-testid="welcome-screen-headline"
          className="font-display text-3xl sm:text-4xl font-bold text-slate-800 leading-tight mb-5"
        >
          {isFirst ? `Welcome, ${firstName}! 🌟` : `Welcome back, ${firstName}! 👋`}
        </h1>

        <p
          data-testid="welcome-screen-message"
          className="text-base text-slate-600 leading-relaxed mb-4"
        >
          {message}
        </p>

        {!isFirst && (
          <p
            data-testid="welcome-screen-line3"
            className="text-sm text-slate-500 leading-relaxed mb-5"
          >
            Every day you show up is a step forward. Let&apos;s make today count.
          </p>
        )}

        <p
          data-testid="welcome-screen-osaat"
          className="text-xs text-slate-400 italic mb-9"
          style={{ letterSpacing: "0.05em" }}
        >
          One Step At A Time.
        </p>

        {showButton && (
          <button
            data-testid="welcome-screen-lets-go"
            onClick={doNavigate}
            className="fade-up inline-flex items-center gap-2 px-8 py-3 rounded-full font-semibold text-sm text-white"
            style={{
              background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
              boxShadow: "0 4px 18px rgba(245,158,11,0.30)",
              transition: "transform 0.18s ease, box-shadow 0.18s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 6px 24px rgba(245,158,11,0.42)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "";
              e.currentTarget.style.boxShadow = "0 4px 18px rgba(245,158,11,0.30)";
            }}
          >
            Let&apos;s Go <span aria-hidden="true">→</span>
          </button>
        )}
      </div>
    </div>
  );
}
